import { Grid, useTheme, alpha } from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import { CameraAlt as CameraAltIcon, PhotoLibrary as PhotoLibraryIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    PIE_COLORS,
    renderPieLabel,
    makeTooltipStyle,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsScreenshotsSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();

    const shots = data.screenshots;

    // Nothing captured in range and no distribution data — hide the section entirely.
    if (
        shots.total === 0 &&
        shots.byDevice.length === 0 &&
        shots.byBrowser.length === 0 &&
        shots.byWidth.every((b) => b.count === 0)
    ) {
        return null;
    }

    const perDay = shots.perDay.map(({ day, count }) => ({
        day: new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        count,
    }));

    return (
        <MetricsSectionHeading
            icon={<CameraAltIcon fontSize="small" />}
            title={LL.metricsPage.sections.screenshots()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* Total captures */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.screenshotsTaken()}
                        value={shots.total}
                        icon={<PhotoLibraryIcon />}
                        color="info.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>

                {/* Device distribution */}
                {shots.byDevice.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.screenshotsByDevice()}
                            height={220}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={shots.byDevice}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        dataKey="count"
                                        nameKey="device"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {shots.byDevice.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tt} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Browser distribution */}
                {shots.byBrowser.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.screenshotsByBrowser()}
                            height={220}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={shots.byBrowser}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        dataKey="count"
                                        nameKey="browser"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {shots.byBrowser.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tt} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Screen width buckets */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.screenshotsByWidth()}
                        height={220}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={shots.byWidth} margin={{ left: 0, right: 16, top: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis
                                    dataKey="bucket"
                                    tick={{ ...tickStyle, fontSize: 10 }}
                                    interval={0}
                                />
                                <YAxis allowDecimals={false} tick={tickStyle} />
                                <Tooltip {...tt} />
                                <Bar
                                    dataKey="count"
                                    fill={theme.palette.info.main}
                                    radius={[3, 3, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Captures per day */}
                {perDay.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.screenshotsPerDay()}
                            height={240}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={perDay} margin={{ left: 0, right: 16, top: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.info.main}
                                        radius={[3, 3, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
